import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronUp, ChevronDown, X, GripVertical } from 'lucide-react';
import PhotoUpload from './PhotoUpload';
import AddressAutocomplete from './AddressAutocomplete';
import BulkUploadButton from './BulkUploadButton';
import { useSiteData } from '@/context/SiteDataContext';

// fields: [{key, label, type: 'text'|'textarea'|'number'|'photo', photoCategory}]
export default function GenericEditor({ title, endpoint, fields, imageKey = 'image', category = 'misc', bulkKey = null }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const { refresh } = useSiteData();

  const emptyRecord = () => {
    const r = { order: 999 };
    fields.forEach(f => { r[f.key] = f.type === 'number' ? 0 : ''; });
    return r;
  };

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch { toast.error(`Failed to load ${title.toLowerCase()}`); }
  };
  useEffect(() => { load(); }, [endpoint]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...editing };
      if (editing.id) await api.patch(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing.id ? 'Updated' : 'Added');
      setEditing(null);
      await load();
      refresh?.();
    } catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await api.delete(`${endpoint}/${id}`);
    toast.success('Deleted');
    await load();
    refresh?.();
  };

  const persistOrder = async (list) => {
    setItems(list);
    await Promise.all(list.map((it, i) => api.patch(`${endpoint}/${it.id}`, { order: i })));
    await load();
    refresh?.();
  };

  const reorder = async (id, delta) => {
    const idx = items.findIndex(i => i.id === id);
    const swapIdx = idx + delta;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    const next = [...items];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    await persistOrder(next);
  };

  const dropAt = async (to) => {
    if (dragIdx === null || dragIdx === to) return;
    const next = [...items];
    const [m] = next.splice(dragIdx, 1);
    next.splice(to, 0, m);
    setDragIdx(null);
    await persistOrder(next);
  };

  const bulkCreate = async (urls) => {
    const start = items.length;
    for (let i = 0; i < urls.length; i++) await api.post(endpoint, { [bulkKey]: urls[i], order: start + i });
    await load();
    refresh?.();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h3 className="font-serif text-xl text-royal font-bold">{title} ({items.length})</h3>
        <div className="flex items-center gap-2">
          {bulkKey && <BulkUploadButton category={category} onDone={bulkCreate} testid={`${category}-bulk-upload`} />}
          <button onClick={() => setEditing(emptyRecord())} data-testid={`${category}-add-btn`} className="btn-royal text-sm py-2 px-4 flex items-center gap-1"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>
      <div className="text-xs text-charcoal-muted mb-2">Drag rows to reorder.</div>
      <div className="grid gap-3">
        {items.map((l, i) => (
          <div key={l.id} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropAt(i)}
            data-testid={`${category}-row-${i}`}
            className={`bg-white border rounded-lg p-3 flex items-center gap-3 ${dragIdx === i ? 'opacity-40' : ''}`}>
            <GripVertical className="w-4 h-4 text-charcoal-muted cursor-grab shrink-0" />
            {imageKey && l[imageKey] && (
              <img src={l[imageKey]} alt="" className="w-14 h-14 object-cover rounded flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-royal truncate">{l[fields[0].key]}</div>
              <div className="text-xs text-charcoal-muted truncate">{l[fields[1]?.key] || ''}</div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => reorder(l.id, -1)} disabled={i === 0} className="p-2 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
              <button onClick={() => reorder(l.id, 1)} disabled={i === items.length - 1} className="p-2 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
              <button onClick={() => setEditing({ ...l })} className="text-xs px-3 py-1.5 bg-royal text-white rounded">Edit</button>
              <button onClick={() => remove(l.id)} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {!items.length && <div className="text-sm text-charcoal-muted">Nothing here yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
              <h3 className="font-serif text-xl text-royal font-bold">{editing.id ? 'Edit' : 'Add'} {title.replace(/s$/, '')}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              {fields.map(f => (
                <div key={f.key}>
                  {f.type === 'photo' ? (
                    <PhotoUpload value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} label={f.label} category={f.photoCategory || category} accept={f.accept} />
                  ) : f.type === 'address' ? (
                    <>
                      <div className="text-sm font-semibold text-royal mb-1">{f.label}</div>
                      <AddressAutocomplete
                        value={editing[f.key] || ''}
                        onChange={(v) => setEditing({ ...editing, [f.key]: v })}
                        onSelect={({ address, lat, lng }) => setEditing({ ...editing, [f.key]: address, lat, lng })}
                      />
                      <div className="text-[11px] text-charcoal-muted mt-1">Start typing to see suggestions. Selecting one fills in latitude &amp; longitude automatically.</div>
                    </>
                  ) : f.type === 'boolean' ? (
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={editing[f.key] !== false}
                        onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })}
                        className="w-4 h-4 accent-royal"
                        data-testid={`toggle-${f.key}`}
                      />
                      <span className="text-sm font-semibold text-royal">{f.label}</span>
                    </label>
                  ) : f.type === 'nav_parent' ? (
                    <>
                      <div className="text-sm font-semibold text-royal mb-1">{f.label}</div>
                      <select
                        value={editing[f.key] || ''}
                        onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                        data-testid={`nav-parent-select`}
                        className="w-full px-3 py-2 border rounded"
                      >
                        <option value="">— None (top-level) —</option>
                        {items.filter(i => i.id !== editing.id && !i.parent_id).map(i => (
                          <option key={i.id} value={i.id}>{`${i.label} → ${i.href}`}</option>
                        ))}
                      </select>
                      <div className="text-[11px] text-charcoal-muted mt-1">Pick a parent to make this item appear inside that dropdown menu.</div>
                    </>
                  ) : f.type === 'checkbox' ? (
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input type="checkbox" checked={!!editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} className="w-4 h-4 accent-royal" data-testid={`checkbox-${f.key}`} />
                      <span className="text-sm font-semibold text-royal">{f.label}</span>
                    </label>
                  ) : f.type === 'color' ? (
                    <label className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-royal">{f.label}</span>
                      <input type="color" value={editing[f.key] || '#ffffff'} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} />
                      {editing[f.key] && <button type="button" onClick={() => setEditing({ ...editing, [f.key]: '' })} className="text-xs underline">Reset</button>}
                    </label>
                  ) : f.type === 'date' ? (
                    <>
                      <div className="text-sm font-semibold text-royal mb-1">{f.label}</div>
                      <input type="date" value={editing[f.key] || ''} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="w-full px-3 py-2 border rounded" />
                    </>
                  ) : f.type === 'textarea' ? (
                    <>
                      <div className="text-sm font-semibold text-royal mb-1">{f.label}</div>
                      <textarea rows={f.rows || 4} value={editing[f.key] || ''} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="w-full px-3 py-2 border rounded" />
                    </>
                  ) : (
                    <>
                      <div className="text-sm font-semibold text-royal mb-1">{f.label}</div>
                      <input type={f.type === 'number' ? 'number' : 'text'} step={f.step} value={editing[f.key] ?? ''} onChange={(e) => setEditing({ ...editing, [f.key]: f.type === 'number' ? parseFloat(e.target.value) : e.target.value })} className="w-full px-3 py-2 border rounded" />
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-charcoal-muted">Cancel</button>
              <button onClick={save} disabled={busy} className="btn-royal">{busy ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
